import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { clsx } from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import PatientStatsTab from './PatientStatsTab';
import FinancialBillingTab from './FinancialBillingTab';
import FinancialPaymentsTab from './FinancialPaymentsTab';
import CrossAnalysisTab from './CrossAnalysisTab';
import ExecutiveReportTab from './ExecutiveReportTab';
import DentalHealthTab from './DentalHealthTab';
import GuineaContextTab from './GuineaContextTab';
import OperationalTab from './OperationalTab';
import DateRangeFilter from '../common/DateRangeFilter';
import { hasAnyRole } from '../../utils/roles';

const tabs = [
  { name: 'Statistiques Patients', icon: '👥' },
  { name: 'Facturation', icon: '💰' },
  { name: 'Encaissements', icon: '💳' },
  { name: 'Analyses Croisées', icon: '📈' },
  { name: 'Reporting Exécutif', icon: '🎯' },
  { name: 'Santé Dentaire', icon: '🦷' },
  { name: 'Contexte Guinéen', icon: '🇬🇳' },
  { name: 'Opérations', icon: '⚙️' },
];

export default function DashboardLayout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState<{ start_date?: string; end_date?: string }>({});

  useEffect(() => {
    // Accès réservé aux administrateurs et comptables
    if (user && !hasAnyRole(user, ['admin', 'comptable'])) {
      alert('Vous n\'avez pas accès au tableau de bord.');
      navigate('/planning');
    }
  }, [user, navigate]);

  if (user && !hasAnyRole(user, ['admin', 'comptable'])) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="py-2">
        <div className="w-full">
          {/* Header */}
          <div className="lg:flex lg:items-center lg:justify-between mb-8">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Dashboard Médical
              </h2>
              <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <span>Analyses et statistiques en temps réel</span>
                </div>
              </div>
            </div>
            <div className="mt-5 flex lg:mt-0 lg:ml-4">
              <DateRangeFilter onChange={setDateRange} />
            </div>
          </div>

          {/* Tabs */}
          <Tab.Group>
            <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-8">
              {tabs.map((tab) => (
                <Tab
                  key={tab.name}
                  className={({ selected }) =>
                    clsx(
                      'w-full rounded-lg py-2.5 px-3 text-sm font-medium leading-5',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                      selected
                        ? 'bg-white text-blue-700 shadow'
                        : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
                    )
                  }
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>{tab.icon}</span>
                    <span className="hidden sm:block">{tab.name}</span>
                  </div>
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <PatientStatsTab dateRange={dateRange} />
              </Tab.Panel>
              <Tab.Panel>
                <FinancialBillingTab dateRange={dateRange} />
              </Tab.Panel>
              <Tab.Panel>
                <FinancialPaymentsTab dateRange={dateRange} />
              </Tab.Panel>
              <Tab.Panel>
                <CrossAnalysisTab dateRange={dateRange} />
              </Tab.Panel>
              <Tab.Panel>
                <ExecutiveReportTab dateRange={dateRange} />
              </Tab.Panel>
              <Tab.Panel>
                <DentalHealthTab dateRange={dateRange} />
              </Tab.Panel>
              <Tab.Panel>
                <GuineaContextTab dateRange={dateRange} />
              </Tab.Panel>
              <Tab.Panel>
                <OperationalTab dateRange={dateRange} />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
}